import React, { useEffect, useState } from 'react';
import { useCreateTask, useUpdateTask } from './useTasksQuery.js';
import { useLists, useCreateList } from '../lists/useListsQuery.js';
import type { Task } from './tasksApi.js';
import { todayLocal } from '../../lib/date.js';

interface Props {
  task?: Task;
  listId?: string;
  parentTaskId?: string;
  onClose: () => void;
}

export default function TaskForm({ task, listId, parentTaskId, onClose }: Props) {
  const isEdit = !!task;
  const { data: lists = [], isLoading: listsLoading } = useLists();
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const createList = useCreateList();

  const [title, setTitle] = useState(task?.title ?? '');
  const [note, setNote] = useState(task?.note ?? '');
  const [priority, setPriority] = useState<Task['priority']>(task?.priority ?? 'medium');
  const [dueDate, setDueDate] = useState(task?.dueDate?.slice(0, 10) ?? '');
  const [selectedListId, setSelectedListId] = useState(task?.listId ?? listId ?? '');
  const [dueDateError, setDueDateError] = useState('');
  const [newListName, setNewListName] = useState('');

  const today = todayLocal();

  // Lists load async, so the initial state above can miss them; adopt the
  // first list once they arrive if nothing more specific was already
  // chosen. Also picks up the list just created via the empty-state prompt
  // below, once it comes back through useLists().
  useEffect(() => {
    if (selectedListId || lists.length === 0) return;
    setSelectedListId(lists[0].listId);
  }, [lists, selectedListId]);

  const pending = createTask.isPending || updateTask.isPending;

  async function handleCreateList(e: React.FormEvent) {
    e.preventDefault();
    if (!newListName.trim()) return;
    await createList.mutateAsync({ name: newListName.trim() });
    setNewListName('');
  }

  // A user can end up with zero lists (deleting their last one) — task
  // creation requires a listId, so show a way to make one instead of a
  // dropdown with nothing in it and a submit button that silently does
  // nothing. Only for new tasks: editing an existing task always has one
  // (its own) even if it's since become the user's only list.
  if (!isEdit && !listsLoading && lists.length === 0) {
    return (
      <form className="task-form" onSubmit={handleCreateList}>
        <p className="task-form__empty-lists-hint">
          You'll need a list before adding tasks — create one to get started.
        </p>
        <div className="task-form__field">
          <label htmlFor="newListName">List name</label>
          <input
            id="newListName"
            type="text"
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            placeholder="e.g. My Task List"
            autoFocus
            required
          />
        </div>
        <div className="task-form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={createList.isPending}>
            {createList.isPending ? 'Creating...' : 'Create List'}
          </button>
        </div>
      </form>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !selectedListId) return;

    if (dueDate && dueDate < today) {
      setDueDateError("Task due date can't be in past");
      return;
    }
    setDueDateError('');

    if (isEdit && task) {
      await updateTask.mutateAsync({
        taskId: task.taskId,
        payload: {
          title: title.trim(),
          note: note || null,
          priority,
          dueDate: dueDate ? new Date(dueDate).toISOString() : null,
          listId: selectedListId,
        },
      });
    } else {
      await createTask.mutateAsync({
        title: title.trim(),
        listId: selectedListId,
        note: note || undefined,
        priority,
        dueDate: dueDate ? new Date(dueDate).toISOString() : undefined,
        parentTaskId: parentTaskId ?? undefined,
      });
    }
    onClose();
  }

  return (
    <form className="task-form" onSubmit={handleSubmit}>
      <div className="task-form__field">
        <label htmlFor="title">Title</label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What needs to be done?"
          autoFocus
          required
        />
      </div>

      <div className="task-form__row">
        <div className="task-form__field">
          <label htmlFor="list">List</label>
          <select
            id="list"
            value={selectedListId}
            onChange={(e) => setSelectedListId(e.target.value)}
            disabled={listsLoading}
            required
          >
            {lists.map((l) => (
              <option key={l.listId} value={l.listId}>
                {l.name}
              </option>
            ))}
          </select>
        </div>

        <div className="task-form__field">
          <label htmlFor="priority">Priority</label>
          <select
            id="priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as Task['priority'])}
          >
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        <div className="task-form__field">
          <label htmlFor="dueDate">Due Date</label>
          <input
            id="dueDate"
            type="date"
            min={today}
            value={dueDate}
            onChange={(e) => {
              setDueDate(e.target.value);
              setDueDateError('');
            }}
          />
          {dueDateError && <span className="task-form__error">{dueDateError}</span>}
        </div>
      </div>

      <div className="task-form__field">
        <label htmlFor="note">Note</label>
        <textarea
          id="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note..."
          rows={3}
        />
      </div>

      <div className="task-form__actions">
        <button type="button" className="btn btn--ghost" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn--primary" disabled={pending || !selectedListId}>
          {pending ? 'Saving...' : isEdit ? 'Update Task' : 'Create Task'}
        </button>
      </div>
    </form>
  );
}
